import { Platform } from '@angular/cdk/platform';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { debounceTime, distinctUntilChanged, forkJoin, of, Subscription, takeUntil, map, Subject } from 'rxjs';

import { Customer, CustomerApi, PersonOfInteresetApi, StandardMedia, StatisticsApi, Tag } from '@api/index';
import { PermissionsService } from '@core/services/permissions.service';
import { MediaService } from '@core/services/media.service';
import { ConfirmationModalComponent } from '@shared/confirmation-modal/confirmation-modal.component';
import { PaginatorParams } from '@api/models/paginator';
import { PersonOfInterestResponse } from '@api/models/person-of-interest';
import { ActiveReportEnum } from '@api/models/active-report-enum';
import { TagsApi } from '@api/services/tags-api.service';
import { AuthService } from '../../core/auth/auth.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { MediaModalService } from '../../core/services/media-modal.service';
import { PoiFiltersModalComponent } from './poi-filters-modal/poi-filters-modal.component';
import { PoiParams, PersonOfInterestListItem, PoiFilterFormGroup, PoiFilterValues } from './model/poi.model';
import { SidePanelService } from '@shared/components/side-panel/side-panel.service';
import { Permissions } from '@shared/models/enums/permissions.enum';
import { ManualPage } from '@shared/models/enums/manualPage.enum';

@Component({
  selector: 'flow-person-of-interest',
  templateUrl: './person-of-interest.component.html',
  styleUrls: ['./person-of-interest.component.scss'],
})
export class PersonOfInterestComponent implements OnInit, OnDestroy {
  @Input() isModal: boolean;
  @Input() customerIdModal: number;
  @ViewChild(MatSort) sort: MatSort;
  private destroy$ = new Subject<void>();
  displayedColumns: string[] = ['id', 'media', 'name', 'category', 'tags', 'fileName', 'customer', 'size', 'created', 'actions'];
  dataSource: MatTableDataSource<PersonOfInterestListItem>;
  panelOpenState = true;
  tags: Tag[];
  categories: Tag[];
  customers: Customer[];
  filterForm: FormGroup<PoiFilterFormGroup>;
  filterRequest: Subscription;
  customerId: number;
  filterValues: PoiFilterValues;
  isAdmin: boolean;
  isAllSelected = false;
  hideBox = false;
  poiIds: number[] = [];
  selectedPois = [];
  numberOfPois: number;
  numberOfMaxPois = 25;
  selectedTabIndex: number = 0;
  ActiveReportEnum = ActiveReportEnum;
  defaultParams: PaginatorParams = {
    pageNumber: 0,
    pageSize: 10,
    sortBy: 'name',
    sortDirection: 'asc',
  };
  params: PoiParams = { ...this.defaultParams };
  totalCount: number = 0;
  filtersNumber: number = 0;
  pageSizeOptions = [5, 10, 25, 100];
  disablePoiInsights: boolean = false;
  disablePoiCategoriesInsights: boolean = false;
  disablePoiReportsInsights: boolean = false;
  deletedPOI: boolean = false;
  Permissions = Permissions;

  constructor(
    private customerApi: CustomerApi,
    private tagsApi: TagsApi,
    private personOfInterestApi: PersonOfInteresetApi,
    private mediaService: MediaService,
    public dialog: MatDialog,
    public platform: Platform,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private statisticsServiceApi: StatisticsApi,
    private mediaModalService: MediaModalService,
    private router: Router,
    private permissionsService: PermissionsService,
    private activatedRoute: ActivatedRoute,
    private sidePanelService: SidePanelService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.paramMap.pipe(map(() => window.history.state)).subscribe(state => {
      if (state?.page) this.selectedTabIndex = state.page;
    });
    if (!this.permissionsService.hasPermissions([Permissions.ViewPoiInsights])) {
      this.disablePoiInsights = true;
    }
    if (!this.permissionsService.hasPermissions([Permissions.ViewPoiCategoriesInsights])) {
      this.disablePoiCategoriesInsights = true;
    }
    if (!this.permissionsService.hasPermissions([Permissions.ViewPoiReportsInsights])) {
      this.disablePoiReportsInsights = true;
    }

    const user = this.authService.userData;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId;
    this.customerId = user?.currentCustomerId || user?.customerId;
    this.initFilter();
    this.getMultipleSelectFilterData();
    if (!this.isAdmin) {
      this.getMultipleSelectFilterData();
      this.displayedColumns.unshift('checkbox');
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'customer');
      this.filterForm.get('customerIds').setValue([this.customerId]);
      this.getPersonOfInterests({ ...this.params, customerIds: [this.customerId] });
    } else {
      if (this.displayedColumns[0] === 'checkbox') {
        this.displayedColumns.shift();
      } else {
        this.getPersonOfInterests(this.params);
      }
    }
  }

  getPersonOfInterests(params?: PoiParams): void {
    this.personOfInterestApi
      .getPersonOfInterests(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe((personOfInterestsResponse: PersonOfInterestResponse) => {
        this.numberOfPois = personOfInterestsResponse.numberOfPois;
        this.numberOfMaxPois = personOfInterestsResponse.numberOfMaxPois;
        this.params = { ...this.params, pageNumber: personOfInterestsResponse?.currentPage };
        this.totalCount = personOfInterestsResponse.totalCount;
        const personOfInterests = personOfInterestsResponse.items.map(poi => {
          const listItem = poi as PersonOfInterestListItem;
          let mediaFile = {
            size: poi.size,
            name: poi.name,
            filename: poi.fileName,
            id: poi.id,
            width: poi.width,
            height: poi.height,
            mediaFileStreamingLink: poi.mediaFileStreamingLink,
            mediaFileThumbnailStreamingLink: poi.mediaFileThumbnailStreamingLink,
          };
          listItem.media = this.mediaService.getPoiMedia(mediaFile);
          listItem.createdDate = DateTime.fromISO(poi.createdDate as string)
            .toLocal()
            .toFormat('yyyy-MM-dd HH:mm');
          return listItem;
        }) as PersonOfInterestListItem[];
        this.selectPOIById(personOfInterests);
        this.dataSource = new MatTableDataSource(personOfInterests);
      });
  }

  selectPOIById(poiItems: PersonOfInterestListItem[]): void {
    poiItems.forEach(poi => {
      if (this.poiIds.includes(poi.id)) {
        poi.isSelected = true;
      }
    });
  }

  onDeleteSelected(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.poiIds.length > 0) {
      this.dialog.open(ConfirmationModalComponent, {
        width: '500px',
        data: {
          remove: () => this.onRemovePOI(this.poiIds),
          logic: true,
        },
      });
    }
  }

  handleSortEvent(sort: Sort) {
    this.params = {
      ...this.params,
      ...this.filterValues,
      pageNumber: 0,
      sortBy: sort?.active,
      sortDirection: sort?.direction || (this.params.sortDirection == 'asc' ? 'desc' : 'asc') || 'asc',
    };
    this.getPersonOfInterests(this.getParams());
  }

  selectMedias(event: MatCheckboxChange, poi: PersonOfInterestListItem) {
    if (!poi && event.checked) {
      this.dataSource.data.filter(m => {
        m.isSelected = true;
        this.poiIds.push(m.id);
        this.selectedPois.push(m);
      });
    } else if (!poi && !event.checked) {
      this.dataSource.data.filter(m => {
        m.isSelected = false;
      });
      this.poiIds = [];
      this.selectedPois = [];
    } else if (poi && event.checked) {
      poi.isSelected = event.checked;
      this.poiIds.push(poi.id);
      this.selectedPois.push(poi);
    } else if (poi && !event.checked) {
      poi.isSelected = event.checked;
      let index = this.poiIds.indexOf(poi.id);
      if (index !== -1) {
        this.poiIds.splice(index, 1);
        this.selectedPois.splice(index, 1);
      }
    }
  }

  onDeleteClick(event: Event, personOfInterest: PersonOfInterestListItem): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        remove: () => this.onRemovePOI(personOfInterest.id),
        logic: true,
      },
    });
  }

  openViewMedia(standardMedia: StandardMedia, $event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.mediaModalService.openViewMediaModal(standardMedia);
  }

  isButtonShown() {
    return !this.isAdmin && this.poiIds.length > 0 && this.selectedTabIndex === 0;
  }

  private onRemovePOI(ids) {
    let poisToDelete = Array.isArray(ids) ? ids : [ids];

    this.personOfInterestApi
      .deletePoi(poisToDelete)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialog.closeAll();
        if (!this.isAdmin) {
          this.poiIds = [];
          this.deletedPOI = true;
          this.getPersonOfInterests({ customerIds: [this.customerId] });
          return;
        }
        this.getPersonOfInterests();
      });
  }

  openFilters() {
    const dialogRef = this.dialog.open(PoiFiltersModalComponent, {
      width: '420px',
      data: {
        filterForm: this.filterForm,
        customerIds: this.customers,
        categories: this.categories,
        tags: this.tags,
        isAdmin: this.isAdmin,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.filter) {
        this.filtersNumber = 0;
        const filterValues = this.filterForm.getRawValue();
        this.getPersonOfInterests(this.getFilterParams(filterValues));
      }
    });
  }

  getFilterParams(filterValues): PoiParams {
    const requestParams = Object.keys(filterValues).reduce<PoiParams>((acc, key) => {
      if (filterValues[key] !== null) {
        if (key == 'created') {
          if (filterValues[key]?.start !== null) {
            acc['dateFrom'] = filterValues[key].start;
          }
          if (filterValues[key]?.end !== null) {
            acc['dateTo'] = filterValues[key].end;
          }
        } else {
          acc[key] = filterValues[key];
          if (key != 'customerIds' || (key == 'customerIds' && !this.customerId)) {
          }
        }
      }
      if (this.customerId) {
        acc['customerIds'] = [this.customerId];
      }
      return acc;
    }, {});

    const hasCustomerIds = !this.customerId && !!requestParams.customerIds?.length;

    this.filtersNumber =
      (requestParams.dateFrom || requestParams.dateTo ? 1 : 0) +
      (requestParams.text ? 1 : 0) +
      (Array.isArray(requestParams.categories) && requestParams.categories.length ? 1 : 0) +
      (Array.isArray(requestParams.tags) && requestParams.tags.length ? 1 : 0) +
      (hasCustomerIds ? 1 : 0);

    const { sortBy, sortDirection } = this.params;

    const mergedParams = {
      ...requestParams,
      ...this.defaultParams,
      ...(sortBy && { sortBy }),
      ...(sortDirection && { sortDirection }),
    };

    return mergedParams;
  }

  private getParams(): PoiParams {
    let params: PoiParams = { ...this.params };

    if (this.filterValues) {
      params = { ...params, ...this.filterValues };
    }

    if (this.customerId) {
      params.customerIds = [this.customerId];
    }

    return params;
  }

  handlePageEvent(e: PageEvent) {
    this.params = { ...this.params, ...this.filterValues, pageNumber: e.pageIndex, pageSize: e.pageSize };
    this.getPersonOfInterests(this.getParams());
  }

  showCreatePoiModal(event, poiID?) {
    event.stopPropagation();
    this.router.navigate(['/person-of-interest', poiID]);
  }

  private initFilter(): void {
    this.filterForm = this.formBuilder.group({
      text: this.formBuilder.control(null),
      categories: this.formBuilder.control(null),
      tags: this.formBuilder.control(null),
      customerIds: this.formBuilder.control(null),
      created: this.formBuilder.control(null),
    });
    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged()).subscribe(this.onFilterChanged);
  }

  private getMultipleSelectFilterData(): void {
    this.filterRequest?.unsubscribe();
    this.filterRequest = forkJoin([
      this.tagsApi.getTags({ customerIds: this.customerId ? [this.customerId] : this.filterValues?.customerIds }),
      this.statisticsServiceApi.getStatisticsCategories({ customerIds: this.customerId ? [this.customerId] : [] }),
      this.isAdmin ? this.customerApi.getCustomers() : of(null),
      !this.isAdmin ? this.customerApi.getCustomerById(this.customerId) : of(null),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tags, categories, customers, customer]) => {
        this.tags = tags;
        this.categories = categories;
        this.customers = customers || [customer];
      });
  }

  private onFilterChanged = (values: PoiFilterValues): void => {
    this.filterValues = values;
  };

  tabChange($event: MatTabChangeEvent): void {
    this.selectedTabIndex = $event.index;
  }

  toggleSidePanel() {
    const config = {
      title: 'contextualModal.poi.title',
      manualKey: ManualPage.Poi,
      children: [
        {
          title: 'contextualModal.poi.whatIsThis.title',
          description: 'contextualModal.poi.whatIsThis.description',
        },
        {
          title: 'contextualModal.poi.whyIsItImportant.title',
          description: 'contextualModal.poi.whyIsItImportant.description',
        },
        {
          title: 'contextualModal.poi.howDoIUseThisFeature.title',
          description: 'contextualModal.poi.howDoIUseThisFeature.description',
        },
      ],
    };

    this.sidePanelService.openSidePanel(config);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.filterRequest?.unsubscribe();
  }
}
